import React, { createContext, useContext, useState } from 'react'
import localStorage from '@locmod/local-storage'
import { constants } from 'helpers'
import { useUserContext } from '../user'

// Define the shape of nudge data
export interface NudgeData {
  message?: string
  type?: 'info' | 'warning' | 'success' | 'error'
  [key: string]: any
}

// Define the context shape
type Context = {
  isOpen: boolean
  nudgeData: NudgeData | null
  openNudge: (data?: NudgeData) => void
  closeNudge: () => void
}

// Create the context
const Context = createContext<Context>(null as any)

// Hook to access the context
export const useNudge = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useNudge must be used within a NudgeProvider')
  }
  return context
}

// Nudge provider component
export const NudgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { profileData } = useUserContext()
  const initialIsOpen =
    localStorage.getItem(constants.localStorageKeys.nudgeIsOpen) === 'true'
  let initialNudgeData: NudgeData | null = null
  try {
    const storedData = localStorage.getItem(
      constants.localStorageKeys.nudgeData
    )
    initialNudgeData = profileData?.is_verified
      ? null
      : storedData
        ? JSON.parse(storedData)
        : null
  } catch (error) {
    console.error('Failed to parse nudgeData from localStorage:', error)
    localStorage.removeItem(constants.localStorageKeys.nudgeData)
  }

  const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen)
  const [nudgeData, setNudgeData] = useState<NudgeData | null>(initialNudgeData)

  const openNudge = (data?: NudgeData) => {
    setIsOpen(true)
    setNudgeData(data || null)
    localStorage.setItem(constants.localStorageKeys.nudgeIsOpen, 'true')
    if (data) {
      localStorage.setItem(
        constants.localStorageKeys.nudgeData,
        JSON.stringify(data)
      )
    }
  }

  const closeNudge = () => {
    setIsOpen(false)
    setNudgeData(null)
    localStorage.setItem(constants.localStorageKeys.nudgeIsOpen, 'false')
    localStorage.removeItem(constants.localStorageKeys.nudgeData)
  }

  const context: Context = {
    isOpen,
    nudgeData,
    openNudge,
    closeNudge,
  }

  return <Context.Provider value={context}>{children}</Context.Provider>
}
